import React, { useContext, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useRecoilCallback, useRecoilValue, waitForAll } from 'recoil';
import type { CustomBenchmarkTypeEnum } from 'venn-api';
import {
  analysisViewIdState,
  benchmarkInputName,
  benchmarkInputs,
  benchmarkInputSubject,
  benchmarkInputType,
  blockInfoGraphicType,
  blockSettings,
  getNewBenchmarkInputId,
  isReportState,
  nextBenchmarkInputNameState,
  blockBenchmarkInput,
  selectedBlocksState,
  type BenchmarkInputId,
} from 'venn-state';
import { analyticsService, hasDisabledBenchmark, useHasFF, useModal } from 'venn-utils';
import Benchmark from './Benchmark';
import InputTitle from './InputTitle';
import { PanelHeader } from './PanelHeader';
import { useSingleInputSelection } from './useSingleInputSelection';
import { BenchmarkEditor, UserContext } from 'venn-components';
import { Button } from '@/components/ui/button';
import { Icon } from 'venn-ui-kit';
import { isNil } from 'lodash';

const DEFAULT_BENCHMARK_TYPE: CustomBenchmarkTypeEnum = 'COMMON';

const BenchmarksSection = () => {
  const hasBlockAssignment = useHasFF('block_assignment_ff');
  const { hasPermission } = useContext(UserContext);
  const readonly = !hasPermission('STUDIO_EDIT') || !hasPermission('STUDIO_EDIT_BENCHMARKS');
  const viewId = useRecoilValue(analysisViewIdState);
  const isReport = useRecoilValue(isReportState);
  const inputIds = useRecoilValue(benchmarkInputs);
  const [isEditorOpen, openEditor, closeEditor] = useModal();

  const addNewBenchmark = useRecoilCallback(
    ({ set, snapshot }) =>
      () => {
        const newId = getNewBenchmarkInputId();
        const newNameLoadable = snapshot.getLoadable(nextBenchmarkInputNameState).valueOrThrow();

        analyticsService.inputsBenchmarkGroupCreated({
          name: newNameLoadable,
          type: DEFAULT_BENCHMARK_TYPE,
          benchmark: undefined,
          relative: false,
          sourcePage: isReport ? 'REPORT_LAB' : 'STUDIO',
          viewId,
        });

        set(benchmarkInputName(newId), newNameLoadable);
        set(benchmarkInputSubject(newId), undefined);
        set(benchmarkInputType(newId), DEFAULT_BENCHMARK_TYPE);
        set(benchmarkInputs, [...inputIds, newId]);
      },
    [inputIds, isReport, viewId],
  );

  const isBenchmarkDisabled = useRecoilCallback(
    ({ snapshot }) =>
      (blockId: string) => {
        const blockSetting = snapshot.getLoadable(blockSettings(blockId)).valueOrThrow();
        const blockGraphicType = snapshot.getLoadable(blockInfoGraphicType(blockId)).valueOrThrow();
        return hasDisabledBenchmark(blockSetting.customBlockType, blockGraphicType);
      },
    [],
  );

  const { inputsState, selectedInput, onInputSelect, disabled } = useSingleInputSelection(
    benchmarkInputs,
    blockBenchmarkInput,
    isBenchmarkDisabled,
  );

  const selectedBlocks = useRecoilValue(selectedBlocksState);
  const blockSelections = useRecoilValue(waitForAll(selectedBlocks.map((id) => blockBenchmarkInput(id))));

  const noneState = useMemo(() => {
    if (selectedBlocks.length === 0) return 'unchecked';

    const blocksWithBenchmarkSupport = selectedBlocks.filter((blockId) => !isBenchmarkDisabled(blockId));

    if (blocksWithBenchmarkSupport.length === 0) {
      return 'unchecked';
    }

    const benchmarkSelectionsForSupportedBlocks = blocksWithBenchmarkSupport.map((blockId) => {
      const blockIndex = selectedBlocks.indexOf(blockId);
      return blockSelections[blockIndex];
    });

    const allUndefinedOrNull = benchmarkSelectionsForSupportedBlocks.every((selection) => isNil(selection));
    if (allUndefinedOrNull) return 'checked';

    const someUndefinedOrNull = benchmarkSelectionsForSupportedBlocks.some((selection) => isNil(selection));
    if (someUndefinedOrNull) return 'indeterminate';

    return 'unchecked';
  }, [blockSelections, selectedBlocks, isBenchmarkDisabled]);

  const radioValue = noneState === 'checked' ? 'none' : selectedInput || '';

  const handleRadioChange = (value: BenchmarkInputId) => {
    if (value === 'none') {
      onInputSelect(undefined);
    } else {
      onInputSelect(value);
    }
  };

  const onAddNew = hasBlockAssignment ? openEditor : addNewBenchmark;

  return hasBlockAssignment ? (
    <>
      <PanelHeader.Root>
        <PanelHeader.Text>Benchmarks</PanelHeader.Text>
        <PanelHeader.NewButton title="Benchmarks" onAddNew={onAddNew} numberOfInputs={inputIds.length} />
      </PanelHeader.Root>
      {isEditorOpen && <BenchmarkEditor benchmarkId={undefined} onClose={closeEditor} />}
      <RadioGroup
        value={radioValue}
        onValueChange={handleRadioChange}
        className="flex flex-col gap-0 divide-y divide-x-0 divide-solid divide-venn-grey-30"
        disabled={readonly || disabled}
      >
        {/* "None" has its own dummy state to show similar checked/unchecked/indeterminate states */}
        <div className="flex items-center gap-[4px] px-[12px] py-[8px] hover:bg-venn-grey-10">
          <div className="h-[19px] flex items-center">
            <RadioGroupItem value="none" id="benchmark-none" state={noneState} disabled={readonly || disabled} />
          </div>
          <Label htmlFor="benchmark-none" className="flex-grow cursor-pointer text-[14px]">
            None
          </Label>
        </div>
        {/* Regular benchmark options */}
        {inputsState.map(({ id, state }) => (
          <div key={id} className="flex items-start gap-[4px] px-[12px] py-[8px] last:mb-[24px] hover:bg-venn-grey-10">
            <div className="h-[19px] flex items-center">
              <RadioGroupItem value={id} id={`benchmark-${id}`} state={state} disabled={readonly || disabled} />
            </div>
            <Label htmlFor={`benchmark-${id}`} className="flex-grow cursor-pointer">
              <Benchmark benchmarkId={id} deletable={inputIds.length > 1} />
            </Label>
          </div>
        ))}
        {inputIds.length === 0 && (
          <Button
            variant="ghost"
            className="flex items-center justify-start gap-[4px] px-[12px] py-[8px] mb-[24px] hover:bg-venn-grey-10 text-venn-dark-blue hover:text-venn-deep-light-blue text-[12px]"
            onClick={onAddNew}
          >
            <Icon prefix="far" type="plus" />
            Add benchmark
          </Button>
        )}
      </RadioGroup>
    </>
  ) : (
    <>
      <InputTitle title="Benchmarks" onAddNew={onAddNew} numberOfInputs={inputIds.length} />
      {inputIds.map((id) => (
        <Benchmark key={id} benchmarkId={id} deletable={inputIds.length > 1} />
      ))}
    </>
  );
};

export default BenchmarksSection;
